import React, { FC, useCallback, useState } from 'react';

import { BUTTON_TYPES, EvButton } from '@evinced-private/ui-common';

import { AzureIssueCreator } from './components/AzureIssueCreator';
import { ADD_TO_AZURE_LABEL } from './consts/AzureConstants';
import { IAzureDevopsTicketCreatorProps } from './types/AzureDevopsTicketCreator.types';
import { useAzureTicketCreator } from './AzureTicketCreatorHooks';
import { AzureTicketCreatorProvider } from './AzureTicketCreatorProvider';

type AzureDevopsTicketCreatorContentProps = Omit<IAzureDevopsTicketCreatorProps, 'config'>;

/**
 * Inner component — renders below the provider, so it can consume the auth client and config
 * via hooks. (A component can't both mount a provider and read its context.)
 *
 * No connection/token state is tracked here — MSAL acquires tokens on demand, so the sign-in
 * popup is triggered the moment the user clicks, right before the form opens.
 */
const AzureDevopsTicketCreatorContent: FC<AzureDevopsTicketCreatorContentProps> = ({
	ticket,
	bypassAzureProcessRules,
	dataAutomationId
}) => {
	const {
		auth,
		config: { logger }
	} = useAzureTicketCreator();
	const [isModalOpen, setModalOpen] = useState(false);

	const openAzureModal = useCallback(async (): Promise<void> => {
		try {
			if (await auth.ensureAzureConnected()) {
				setModalOpen(true);
			}
		} catch (error) {
			logger.error('Failed to open Azure issue creator', error);
		}
	}, [auth, logger]);

	const closeAzureModal = useCallback((): void => {
		setModalOpen(false);
	}, []);

	return (
		<>
			<EvButton
				type={BUTTON_TYPES.PRIMARY}
				onClick={openAzureModal}
				dataAutomationId={dataAutomationId}
			>
				{ADD_TO_AZURE_LABEL}
			</EvButton>
			{isModalOpen && (
				<AzureIssueCreator
					isOpen={isModalOpen}
					closeModal={closeAzureModal}
					ticketData={ticket}
					bypassAzureProcessRules={bypassAzureProcessRules}
				/>
			)}
		</>
	);
};

/**
 * Self-contained "Add to Azure" entry point: the button plus the Azure issue-creation popup
 * (ui-common's AzureIssueCreatorFormUI, wrapped by AzureIssueCreator).
 *
 * Mounts the module's own provider from the given `config`, so consumers don't wire anything
 * into their app tree — they just pass `config` and an already-mapped {@link AzureTicketDraft}.
 */
export const AzureDevopsTicketCreator: FC<IAzureDevopsTicketCreatorProps> = ({
	config,
	...contentProps
}) => (
	<AzureTicketCreatorProvider config={config}>
		<AzureDevopsTicketCreatorContent {...contentProps} />
	</AzureTicketCreatorProvider>
);
